import React, {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {Alert, FlexColumn, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {ReportToolbar} from './ReportToolbar';
import {EmptyReportState} from './EmptyReportState';
import InlineLoader from '../../forecast-app/shared/components/inline-loader/inline_loader';
import {ReportErrorState} from './ReportErrorState';
import {LeaveBuilderPrompt} from './LeaveBuilderPrompt';
import {ReportTable} from './ReportTable/ReportTable';
import {TestHook} from './TestHook';
import {groupingCellPaths, useClientGrouping} from './ReportTable/useClientGrouping';
import {grainRootOf} from './data/definitionFields';
import {AIBanner} from './TopBar/AIBanner';
import {EditTopBar} from './TopBar/EditTopBar';
import {LockStateBanner} from './TopBar/LockStateBanner';
import {PublishStateBanner} from './TopBar/PublishStateBanner';
import {ReportPageHeader} from './TopBar/ReportPageHeader';
import {ViewTopBar} from './TopBar/ViewTopBar';

const tokens = tokensForThemeProvider.sky.sys;

/** Horizontal page gutter (32px in the design's 1920px frame) and vertical rhythm so the page header,
 * controls row, AI banner, and table are all inset from the viewport edges and evenly spaced,
 * rather than full-bleed — see the design. the design's `Page Header`
 * container has asymmetric vertical padding
 * (`paddingTop=8 paddingBottom=16`, not a uniform 16) and `itemSpacing=16` between its children
 * — not the 12px gap previously used here. */
/* DS FlexColumn defaults to flex:1, which would stretch this to fill the full page height (its
 * parent `Surface` fills the viewport); any leftover height would then be redistributed as growth
 * among children that are themselves FlexColumn/FlexRow-based (the header, the top bar), pushing
 * them apart by an amount that shrinks as the table grows with more rows. Pin to content size so
 * the page's vertical rhythm stays fixed regardless of row count. */
/* The loader paints an 80px spinner as a centred background image and sizes itself to its parent, so
 * it needs a slot with real height: this column is auto-height, where `height: 100%` resolves to the
 * loader's own content height, which is zero. Roughly a few table rows' worth, so the surface does not
 * visibly jump when the rows land. */
const LoaderSlot = styled.div`
	min-height: 240px;
	display: flex;
`;

const SurfaceWrapper = styled(FlexColumn)`
	gap: ${tokens.spacing['400']};
	width: 100%;
	padding: ${tokens.spacing['200']} ${tokens.spacing['800']} ${tokens.spacing['400']};
	flex: 0 0 auto;
`;

/** Assembled AI Report Builder surface body: `LockStateBanner` first and full-bleed, outside the
 * padded wrapper so an expired editing session is announced across the whole content area under the
 * global navigation before anything else on the page; then, inside the page gutter, the page header
 * (Reports eyebrow + report name
 * heading/pencil), then the mode/role-dependent top bar, then `PublishStateBanner` — telling the
 * editor whether readers are actually isolated from in-progress edits, and rendering nothing at all
 * for a reader, whose last-published date is a caption on the page header instead — then AIBanner —
 * shown only when the report's
 * origin is AI-generated AND the surface is in edit mode, since showing it for a report opened from
 * anywhere else would claim an origin that isn't true, and a reader has no conditions to reconcile
 * against the prompt it quotes (per the design, the banner otherwise sits BELOW the name/period/Save-Report
 * row, not above it) — then
 * the merged conditions/Group By/Columns `ReportToolbar` strip (edit mode only — view mode goes
 * straight from the top bar to the table, as the design renders it), and the grouped `ReportTable` — or, when the fetch
 * matched zero rows, `EmptyReportState` in its place, stating that nothing matched and how to widen
 * the report instead of a bare empty grid, or `ReportErrorState` when the fetch failed outright
 * (which is not the same thing as matching nothing). This
 * is the surface BODY only — no modal chrome. Row data and its grouping/table state
 * all come from `useReportBuilder()`; grouping is applied client-side via `useClientGrouping`
 * before handing rows to `ReportTable`. Mode switching is done solely via the "Save Report"
 * (edit→view) and "Edit Report" (view→edit) buttons in the top bars — no mode-toggle UI here. */
export function ReportBuilderSurface(): JSX.Element {
	const {
		definition,
		mode,
		source,
		tableState,
		updateTableState,
		toggleFavorite,
		rows,
		totalCount,
		cappedTotalEstimate,
		totalIsFloor,
		capped,
		grouping,
		hasFetched,
		fetchFailed,
		fetchAccessDenied,
		retryFetch,
		taxonomy,
	} = useReportBuilder();
	const {formatMessage} = useIntl();
	const [capBannerDismissed, setCapBannerDismissed] = useState(false);

	// Dismissal applies to the result on screen, not to the report forever. Editing the report
	// re-fetches (a new `definition` identity is what drives the fetch), and that new result can be
	// capped too — so a dismissal that survived would silently hide the truncation.
	useEffect(() => {
		setCapBannerDismissed(false);
	}, [definition]);

	// Grouping reads a cell per row, and which cell a token means is per grain: `client` is
	// `task.client.name` on a task report and `projectStatus.client.name` on a project one.
	const groupingOptions = useMemo(
		() => ({cellPaths: groupingCellPaths(grainRootOf(definition), taxonomy), taxonomy}),
		[definition, taxonomy]
	);
	const groupedRows = useClientGrouping(rows, grouping, groupingOptions);

	const totalLabel = totalIsFloor ? `${totalCount}+` : String(totalCount);
	const capBanner =
		capped && !capBannerDismissed ? (
			<Alert
				type={Alert.TYPE.INFORMATION}
				text={formatMessage({id: 'ai_report_builder.row_cap_banner'}, {shown: cappedTotalEstimate, total: totalLabel})}
				onDismiss={() => setCapBannerDismissed(true)}
			/>
		) : null;

	return (
		<TestHook data-cy={'ai-report-builder-surface'}>
			{/* Outside `SurfaceWrapper` on purpose: the expired-session banner is full-bleed, spanning the
			 * content area directly under the global navigation and above the page header, rather than
			 * sitting inside the page's horizontal gutter like the other banners. */}
			<LockStateBanner />

			<SurfaceWrapper>
				<LeaveBuilderPrompt />

				<ReportPageHeader />

				{mode === 'edit' ? <EditTopBar /> : <ViewTopBar />}

				<PublishStateBanner />

				{source === 'AI' && mode === 'edit' ? <AIBanner /> : null}

				{mode === 'edit' ? <ReportToolbar /> : null}

				{capBanner}

				{fetchFailed ? (
					<ReportErrorState onRetry={retryFetch} accessDenied={fetchAccessDenied} />
				) : !hasFetched ? (
					/* Only a fetch with nothing to show yet gets a placeholder. Once rows have arrived a
					   re-fetch keeps them on screen: flashing a loader over data that is still correct reads
					   as the page resetting rather than as progress, and the rows it replaces are usually the
					   ones being refined.

					   Keyed off `hasFetched` rather than a separate in-flight flag because the fetch starts in
					   an effect, which runs AFTER the first paint — an in-flight flag is still false at that
					   point, so the surface would paint an empty table for one frame before the loader
					   appeared. A failed fetch clears `hasFetched`, so this also covers the retry after an
					   error, where there are again no rows to preserve.

					   `InlineLoader` rather than `UploadingOverlay`: this slot sits inside the page, and the
					   overlay is `position: fixed` over the whole viewport below the top nav, so it would
					   escape this row and cover the toolbar and banner above it — leaving them visible but
					   swallowing every click. Both draw the same spinner. */
					<LoaderSlot data-cy={'ai-report-builder-rows-loading'}>
						<InlineLoader />
					</LoaderSlot>
				) : rows.length === 0 ? (
					<EmptyReportState />
				) : (
					<ReportTable
						rows={groupedRows}
						definition={definition}
						taxonomy={taxonomy}
						tableState={tableState}
						onTableStateChange={updateTableState}
						onToggleFavorite={toggleFavorite}
					/>
				)}
			</SurfaceWrapper>
		</TestHook>
	);
}
